import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  FileText,
  FolderOpen,
  Search,
  Filter,
  Download,
  Upload,
  Eye,
  EyeOff,
  Users,
  Building,
  User,
  CheckCircle,
  Clock,
  FileCheck,
  FilePlus,
  Share2,
  Move,
  Trash2,
  MoreVertical,
  ArrowRight,
  Sparkles,
  ChevronDown,
  X,
  Check,
  Star,
  Zap,
  FolderCheck,
  SearchX,
  Folders,
  MousePointer2,
  LayoutGrid,
  List,
  TrendingUp,
  Send,
  Target,
  Workflow,
} from "lucide-react";
import { PathNavigationSection } from "./PathNavigationSection";
import { ClientVisibilitySection } from "./ClientVisibilitySection";
import "./Document.css";
export default function DocumentManagementFeatureV2() {
  const [activeCategory, setActiveCategory] = useState("clients");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [journeyStep, setJourneyStep] = useState(0);
  const [searchDemo, setSearchDemo] = useState("");
  const [autoOrganizing, setAutoOrganizing] = useState(false);

  // Sample documents data
  const documents = {
    clients: [
      {
        id: 1,
        name: "Project Proposal - TechCorp.pdf",
        client: "TechCorp Inc.",
        size: "2.4 MB",
        date: "2 hours ago",
        type: "pdf",
        sharedWithClient: true,
        fromTask: "Initial Proposal",
        tags: ["Proposal", "Active"],
      },
      {
        id: 2,
        name: "Contract Agreement - StartupXYZ.docx",
        client: "StartupXYZ",
        size: "1.8 MB",
        date: "Yesterday",
        type: "docx",
        sharedWithClient: false,
        fromTask: null,
        tags: ["Contract", "Review"],
      },
      {
        id: 3,
        name: "Invoice #1247 - Acme Corp.pdf",
        client: "Acme Corp",
        size: "890 KB",
        date: "3 days ago",
        type: "pdf",
        sharedWithClient: true,
        fromTask: "Billing Q4",
        tags: ["Invoice", "Paid"],
      },
    ],
    user: [
      {
        id: 5,
        name: "Personal Notes - Q4 Goals.txt",
        size: "45 KB",
        date: "Today",
        type: "txt",
        tags: ["Personal", "Goals"],
      },
      {
        id: 6,
        name: "Resume - Updated 2024.pdf",
        size: "340 KB",
        date: "2 weeks ago",
        type: "pdf",
        tags: ["Personal", "Career"],
      },
    ],
    office: [
      {
        id: 8,
        name: "Company Handbook 2024.pdf",
        size: "4.3 MB",
        date: "Jan 2024",
        type: "pdf",
        tags: ["Policy", "Official"],
      },
      {
        id: 9,
        name: "Team Directory - All Departments.xlsx",
        size: "680 KB",
        date: "2 weeks ago",
        type: "xlsx",
        tags: ["Directory", "HR"],
      },
    ],
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "pdf":
        return "📄";
      case "docx":
        return "📝";
      case "xlsx":
        return "📊";
      case "txt":
        return "📃";
      case "fig":
        return "🎨";
      default:
        return "📄";
    }
  };

  // Auto-play journey animation
  useEffect(() => {
    const interval = setInterval(() => {
      setJourneyStep((prev) => (prev + 1) % 6);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Document journey steps
  const journeySteps = [
    {
      id: 1,
      title: "Upload to Task",
      description: 'Document uploaded to "Client Proposal" task',
      icon: Upload,
      color: "blue",
      detail: "You attach Proposal.pdf while working on your task",
    },
    {
      id: 2,
      title: "Auto-Detection",
      description: "System recognizes the document",
      icon: Sparkles,
      color: "purple",
      detail: "Simpliflyte instantly identifies the file and its context",
    },
    {
      id: 3,
      title: "Smart Categorization",
      description: 'Automatically placed in "Clients" category',
      icon: FolderCheck,
      color: "indigo",
      detail: "File is routed to the correct category based on task type",
    },
    {
      id: 4,
      title: "Task Linking",
      description: "Connected to original task",
      icon: Workflow,
      color: "violet",
      detail: '"From Task" badge shows the connection',
    },
    {
      id: 5,
      title: "Ready to Share",
      description: "Available for client visibility toggle",
      icon: Eye,
      color: "green",
      detail: "Control whether clients see it with one click",
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      {/* <section className="relative min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800 py-32 px-6 overflow-hidden flex items-center">
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                    backgroundSize: '50px 50px'
                }} />

                <div className="absolute top-20 right-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-20 left-1/4 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[100px]" />

                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(6,182,212,0.05),transparent_50%)]" />

                <div className="max-w-7xl mx-auto relative z-10 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                        className="text-center mb-20"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.2 }}
                            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/10 to-cyan-400/10 border-2 border-cyan-500/30 text-cyan-400 px-8 py-3 rounded-full mb-12 backdrop-blur-sm shadow-lg"
                        >
                            <Target className="w-5 h-5" />
                            <span className="text-sm tracking-wide">Enterprise Document Command Center</span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="text-7xl md:text-8xl lg:text-9xl text-white mb-8 tracking-tight leading-[0.95]"
                            style={{ fontWeight: 300 }}
                        >
                            Complete Control.
                            <br />
                            <span className="bg-gradient-to-r from-cyan-400 via-cyan-300 to-cyan-500 bg-clip-text text-transparent" style={{ fontWeight: 400 }}>
                                Zero Effort.
                            </span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed tracking-wide"
                        >
                            Professional document management that works automatically.
                            <br />
                            Powerful search. Intelligent organization. Enterprise control.
                        </motion.p>

                        <motion.div
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ delay: 0.8, duration: 1 }}
                            className="w-32 h-[2px] bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto mt-16 mb-16"
                        />

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1 }}
                            className="flex items-center justify-center gap-16 flex-wrap"
                        >
                            {[
                                { value: '100%', label: 'Automatic' },
                                { value: '<2s', label: 'Search Speed' },
                                { value: '∞', label: 'Documents' },
                            ].map((stat, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.2 + i * 0.1 }}
                                    className="text-center"
                                >
                                    <p className="text-4xl bg-gradient-to-r from-cyan-400 to-cyan-500 bg-clip-text text-transparent mb-2" style={{ fontWeight: 300 }}>
                                        {stat.value}
                                    </p>
                                    <p className="text-sm text-gray-500 uppercase tracking-widest">{stat.label}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </motion.div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-black pointer-events-none" />
            </section> */}

      {/* SECTION 1: WATCH IT ORGANIZE + SEARCH THEATER (Option 1+8 Combined) */}
      {/* <section className="py-32 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <PathNavigationSection />
                </div>
            </section> */}

      {/* SECTION 2: DOCUMENT JOURNEY (Option 5) */}
      {/* <section className="py-32 px-6 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative">
                <div className="absolute inset-0 opacity-30" style={{
                    backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)`,
                    backgroundSize: '100px 100px'
                }} />

                <div className="absolute top-20 right-1/4 w-[500px] h-[500px] bg-cyan-500/20 rounded-full blur-[100px] animate-pulse" />
                <div className="absolute bottom-20 left-1/4 w-[400px] h-[400px] bg-cyan-400/20 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '1s' }} />

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="text-center mb-16">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <div className="inline-flex items-center gap-2 bg-cyan-500/20 border-2 border-cyan-400/50 text-cyan-300 px-5 py-2 rounded-full mb-8 shadow-[0_0_20px_rgba(6,182,212,0.3)]">
                                <Workflow className="w-4 h-4" />
                                <span className="text-sm">Complete Lifecycle</span>
                            </div>
                            <h2 className="text-6xl text-white mb-6 drop-shadow-[0_0_30px_rgba(6,182,212,0.3)]">
                                Follow the
                                <br />
                                <span className="bg-gradient-to-r from-cyan-300 via-cyan-400 to-cyan-300 bg-clip-text text-transparent">
                                    Document Journey
                                </span>
                            </h2>
                            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                                Watch how a single document flows seamlessly from task to organized storage, ready to share.
                            </p>
                        </motion.div>
                    </div>

                    <div className="relative">
                        <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-cyan-400/50 via-cyan-500/80 to-cyan-400/50 hidden md:block shadow-[0_0_10px_rgba(6,182,212,0.5)]" />

                        <div className="space-y-16">
                            {journeySteps.map((step, index) => (
                                <motion.div
                                    key={step.id}
                                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.2 }}
                                    className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                                        }`}
                                >
                                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                                        <motion.div
                                            whileHover={{ scale: 1.03, y: -5 }}
                                            className={`bg-gradient-to-br from-gray-800/90 to-gray-700/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border-2 ${journeyStep === index
                                                ? 'border-cyan-400 shadow-[0_0_30px_rgba(6,182,212,0.4)]'
                                                : 'border-gray-600/50 hover:border-cyan-500/30'
                                                }`}
                                        >
                                            <div className={`flex items-start gap-4 ${index % 2 === 0 ? 'md:flex-row-reverse md:text-right' : ''}`}>
                                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center flex-shrink-0 shadow-lg ${journeyStep === index ? 'shadow-[0_0_20px_rgba(6,182,212,0.6)]' : ''
                                                    }`}>
                                                    <step.icon className="w-7 h-7 text-white" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className="text-sm text-cyan-300 font-medium">
                                                            Step {step.id}
                                                        </span>
                                                    </div>
                                                    <h3 className="text-2xl text-white mb-2">{step.title}</h3>
                                                    <p className="text-gray-300 mb-3">{step.description}</p>
                                                    <p className="text-sm text-gray-400 italic">{step.detail}</p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </div>

                                    <div className="relative z-10 hidden md:block">
                                        <motion.div
                                            animate={{
                                                scale: journeyStep === index ? [1, 1.3, 1] : 1,
                                                boxShadow: journeyStep === index
                                                    ? ['0 0 0 0 rgba(6, 182, 212, 0.7)', '0 0 0 20px rgba(6, 182, 212, 0)', '0 0 0 0 rgba(6, 182, 212, 0.7)']
                                                    : '0 0 10px rgba(6, 182, 212, 0.5)',
                                            }}
                                            transition={{ duration: 1.5, repeat: journeyStep === index ? Infinity : 0 }}
                                            className={`w-6 h-6 rounded-full ${journeyStep === index ? 'bg-cyan-400' : 'bg-cyan-500/80'
                                                }`}
                                            style={{
                                                boxShadow: journeyStep === index ? '0 0 20px rgba(6, 182, 212, 0.8)' : '0 0 10px rgba(6, 182, 212, 0.5)'
                                            }}
                                        />
                                    </div>

                                    <div className="flex-1 hidden md:block" />
                                </motion.div>
                            ))}
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="mt-16 bg-gradient-to-r from-cyan-500/30 via-cyan-400/20 to-cyan-500/30 border-2 border-cyan-400/60 rounded-3xl p-12 text-center relative overflow-hidden backdrop-blur-sm shadow-[0_0_40px_rgba(6,182,212,0.3)]"
                        >
                            <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500/20 via-cyan-400/10 to-transparent blur-3xl rounded-3xl" />

                            <motion.div
                                initial={{ height: 0 }}
                                animate={{ height: 120 }}
                                transition={{ duration: 1.5, ease: "easeOut" }}
                                className="absolute top-0 left-1/2 w-[3px] bg-gradient-to-b from-cyan-400 to-cyan-500 origin-top shadow-[0_0_10px_rgba(6,182,212,0.5)]"
                                style={{ transformOrigin: 'top' }}
                            />

                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 1.5, duration: 0.5, type: "spring" }}
                                className="relative z-10 w-20 h-20 mx-auto mb-6 bg-cyan-500/30 backdrop-blur-sm rounded-full border-4 border-cyan-400 flex items-center justify-center shadow-[0_0_30px_rgba(6,182,212,0.6)]"
                            >
                                <CheckCircle className="w-10 h-10 text-cyan-300" />
                            </motion.div>

                            <motion.h3
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 2 }}
                                className="text-4xl text-white mb-4"
                            >
                                Document Ready!
                            </motion.h3>
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 2.2 }}
                                className="text-xl text-gray-200 mb-8"
                            >
                                Organized, linked, searchable, and ready to share with one click.
                            </motion.p>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 2.4 }}
                                className="flex items-center justify-center gap-6 flex-wrap"
                            >
                                <div className="flex items-center gap-2 text-gray-200">
                                    <Check className="w-5 h-5 text-cyan-300" />
                                    <span>Categorized</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-200">
                                    <Check className="w-5 h-5 text-cyan-300" />
                                    <span>Linked to Task</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-200">
                                    <Check className="w-5 h-5 text-cyan-300" />
                                    <span>Searchable</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-200">
                                    <Check className="w-5 h-5 text-cyan-300" />
                                    <span>Shareable</span>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section> */}

      {/* SECTION 3: INTERACTIVE CATEGORY EXPLORER (Option 6) */}
      {/* <section className="py-32 px-6 bg-white relative">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(6, 182, 212, 0.08) 1px, transparent 1px)`,
            backgroundSize: "30px 30px",
          }}
        />

        <div className="absolute top-40 left-1/3 w-[400px] h-[400px] bg-cyan-50 rounded-full blur-[100px]" />
        <div className="absolute bottom-40 right-1/3 w-[350px] h-[350px] bg-cyan-100/50 rounded-full blur-[80px]" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-cyan-50 border-2 border-cyan-200 text-cyan-700 px-5 py-2 rounded-full mb-8 shadow-sm">
                <LayoutGrid className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Focused Organization
                </span>
              </div>
              <h2 className="text-6xl text-gray-900 mb-6">
                Three Categories.
                <br />
                <span className="bg-gradient-to-r from-cyan-600 via-cyan-500 to-cyan-600 bg-clip-text text-transparent">
                  Infinite Power.
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Click each category to explore its full capabilities. Everything
                you need, perfectly organized.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-3 gap-6 mb-8">
            {[
              { id: "clients", label: "Clients", icon: Users },
              { id: "user", label: "User", icon: User },
              { id: "office", label: "Office", icon: Building },
            ].map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.98 }}
                className={`py-12 px-6 rounded-3xl transition-all relative overflow-hidden ${
                  activeCategory === category.id
                    ? "bg-gradient-to-br from-cyan-500 to-cyan-600 text-white shadow-2xl shadow-cyan-500/30"
                    : "bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 text-gray-600 hover:border-cyan-200 hover:shadow-lg"
                }`}
              >
                {activeCategory === category.id && (
                  <motion.div
                    className="absolute inset-0 bg-white/20 rounded-3xl"
                    animate={{
                      scale: [1, 1.1, 1],
                      opacity: [0.3, 0.1, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                )}

                <motion.div
                  animate={
                    activeCategory === category.id
                      ? {
                          scale: [1, 1.1, 1],
                          rotate: [0, 5, -5, 0],
                        }
                      : {}
                  }
                  transition={{ duration: 0.5 }}
                >
                  <category.icon
                    className={`w-16 h-16 mx-auto mb-4 relative z-10 ${activeCategory === category.id ? "text-white" : "text-gray-400"}`}
                  />
                </motion.div>
                <p className="text-2xl mb-2 relative z-10">{category.label}</p>
                <motion.p
                  className="text-sm opacity-80 relative z-10"
                  animate={
                    activeCategory === category.id
                      ? { scale: [1, 1.05, 1] }
                      : {}
                  }
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  {documents[category.id].length} documents
                </motion.p>
              </motion.button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -30, scale: 0.95 }}
              transition={{ duration: 0.4 }}
              className="bg-gradient-to-br from-gray-50 via-white to-gray-50 backdrop-blur-sm rounded-3xl p-12 border-2 border-gray-200 shadow-xl"
            >
              <div className="flex items-center justify-between mb-8">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <h3 className="text-4xl text-gray-900 mb-2">
                    {activeCategory === "clients"
                      ? "Client Documents"
                      : activeCategory === "user"
                        ? "Personal Workspace"
                        : "Company Resources"}
                  </h3>
                  <p className="text-lg text-gray-600">
                    {activeCategory === "clients"
                      ? "Proposals, contracts, invoices, and shared files"
                      : activeCategory === "user"
                        ? "Your private notes, certificates, and personal files"
                        : "Handbooks, policies, and team documents"}
                  </p>
                </motion.div>
                <motion.div
                  className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1, rotate: 360 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                  {activeCategory === "clients" ? (
                    <Users className="w-10 h-10 text-white" />
                  ) : activeCategory === "user" ? (
                    <User className="w-10 h-10 text-white" />
                  ) : (
                    <Building className="w-10 h-10 text-white" />
                  )}
                </motion.div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {documents[activeCategory].map((doc, index) => (
                  <motion.div
                    key={doc.id}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{
                      delay: index * 0.1,
                      type: "spring",
                      stiffness: 200,
                    }}
                    whileHover={{
                      scale: 1.03,
                      y: -8,
                      boxShadow: "0 20px 40px rgba(6, 182, 212, 0.15)",
                    }}
                    className="bg-white rounded-2xl p-6 shadow-md border-2 border-gray-100 hover:border-cyan-300 transition-all cursor-pointer group relative overflow-hidden"
                  >
                    <motion.div className="absolute inset-0 bg-gradient-to-r from-cyan-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    <div className="flex items-start gap-4 relative z-10">
                      <motion.div
                        className="text-4xl"
                        whileHover={{ scale: 1.2, rotate: [0, -10, 10, 0] }}
                        transition={{ duration: 0.5 }}
                      >
                        {getFileIcon(doc.type)}
                      </motion.div>
                      <div className="flex-1">
                        <p className="text-lg text-gray-900 mb-2 group-hover:text-cyan-700 transition-colors">
                          {doc.name}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                          {activeCategory === "clients" && "client" in doc && (
                            <>
                              <span>{doc.client}</span>
                              <span>•</span>
                            </>
                          )}
                          <span>{doc.size}</span>
                          <span>•</span>
                          <span>{doc.date}</span>
                        </div>
                        {activeCategory === "clients" &&
                          "fromTask" in doc &&
                          doc.fromTask && (
                            <motion.span
                              className="inline-block px-3 py-1 bg-cyan-100 text-cyan-700 border border-cyan-300 rounded-full text-xs"
                              whileHover={{ scale: 1.05 }}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 + 0.2 }}
                            >
                              From: {doc.fromTask}
                            </motion.span>
                          )}
                      </div>
                      <div className="flex flex-col gap-2">
                        {activeCategory === "clients" &&
                          "sharedWithClient" in doc && (
                            <motion.button
                              className={`p-3 rounded-xl transition-all ${
                                doc.sharedWithClient
                                  ? "bg-cyan-500 text-white shadow-md"
                                  : "bg-gray-200 text-gray-500"
                              }`}
                              whileHover={{ scale: 1.1, rotate: 5 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              {doc.sharedWithClient ? (
                                <Eye className="w-5 h-5" />
                              ) : (
                                <EyeOff className="w-5 h-5" />
                              )}
                            </motion.button>
                          )}
                        <motion.button
                          className="p-3 bg-cyan-500 text-white rounded-xl hover:bg-cyan-600 transition-all shadow-md"
                          whileHover={{ scale: 1.1, rotate: -5 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Download className="w-5 h-5" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                className="grid md:grid-cols-3 gap-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {activeCategory === "clients" && (
                  <>
                    <motion.div
                      className="bg-white rounded-xl p-6 border-2 border-gray-100 text-center group hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer"
                      whileHover={{ y: -5 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <Eye className="w-8 h-8 text-cyan-600 mx-auto mb-3" />
                      </motion.div>
                      <p className="text-sm text-gray-700 font-medium">
                        Client Visibility Control
                      </p>
                    </motion.div>
                    <motion.div
                      className="bg-white rounded-xl p-6 border-2 border-gray-100 text-center group hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer"
                      whileHover={{ y: -5 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <Workflow className="w-8 h-8 text-cyan-600 mx-auto mb-3" />
                      </motion.div>
                      <p className="text-sm text-gray-700 font-medium">
                        Task Integration
                      </p>
                    </motion.div>
                    <motion.div
                      className="bg-white rounded-xl p-6 border-2 border-gray-100 text-center group hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer"
                      whileHover={{ y: -5 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <Share2 className="w-8 h-8 text-cyan-600 mx-auto mb-3" />
                      </motion.div>
                      <p className="text-sm text-gray-700 font-medium">
                        One-Click Sharing
                      </p>
                    </motion.div>
                  </>
                )}
                {activeCategory === "user" && (
                  <>
                    <motion.div
                      className="bg-white rounded-xl p-6 border-2 border-gray-100 text-center group hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer"
                      whileHover={{ y: -5 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <EyeOff className="w-8 h-8 text-cyan-600 mx-auto mb-3" />
                      </motion.div>
                      <p className="text-sm text-gray-700 font-medium">
                        Private & Secure
                      </p>
                    </motion.div>
                    <motion.div
                      className="bg-white rounded-xl p-6 border-2 border-gray-100 text-center group hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer"
                      whileHover={{ y: -5 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <FileCheck className="w-8 h-8 text-cyan-600 mx-auto mb-3" />
                      </motion.div>
                      <p className="text-sm text-gray-700 font-medium">
                        Personal Organization
                      </p>
                    </motion.div>
                    <motion.div
                      className="bg-white rounded-xl p-6 border-2 border-gray-100 text-center group hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer"
                      whileHover={{ y: -5 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <Target className="w-8 h-8 text-cyan-600 mx-auto mb-3" />
                      </motion.div>
                      <p className="text-sm text-gray-700 font-medium">
                        Quick Access
                      </p>
                    </motion.div>
                  </>
                )}
                {activeCategory === "office" && (
                  <>
                    <motion.div
                      className="bg-white rounded-xl p-6 border-2 border-gray-100 text-center group hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer"
                      whileHover={{ y: -5 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <Users className="w-8 h-8 text-cyan-600 mx-auto mb-3" />
                      </motion.div>
                      <p className="text-sm text-gray-700 font-medium">
                        Team Collaboration
                      </p>
                    </motion.div>
                    <motion.div
                      className="bg-white rounded-xl p-6 border-2 border-gray-100 text-center group hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer"
                      whileHover={{ y: -5 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <FileText className="w-8 h-8 text-cyan-600 mx-auto mb-3" />
                      </motion.div>
                      <p className="text-sm text-gray-700 font-medium">
                        Official Documents
                      </p>
                    </motion.div>
                    <motion.div
                      className="bg-white rounded-xl p-6 border-2 border-gray-100 text-center group hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer"
                      whileHover={{ y: -5 }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      >
                        <CheckCircle className="w-8 h-8 text-cyan-600 mx-auto mb-3" />
                      </motion.div>
                      <p className="text-sm text-gray-700 font-medium">
                        Always Updated
                      </p>
                    </motion.div>
                  </>
                )}
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section> */}

      {/* SECTION 4: CLIENT VISIBILITY */}
      <ClientVisibilitySection />

      {/* Final CTA Section */}
      {/* <section className="py-32 px-6 bg-black relative overflow-hidden">
        <div className="absolute inset-0  bg-[size:100px_100px]" />

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[120px]" />

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Target className="w-20 h-20 text-cyan-400 mx-auto mb-8" />
            <h2 className="text-6xl md:text-7xl text-white mb-8">
              Professional Power.
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-cyan-300 to-cyan-500 bg-clip-text text-transparent">
                Effortless Control.
              </span>
            </h2>
            <p className="text-2xl text-gray-300 mb-16 leading-relaxed">
              Enterprise-grade document management that works automatically.
              <br />
              No training required. No complexity. Just results.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mt-20">
              {[
                { value: "2s", label: "Find any document", icon: Zap },
                { value: "100%", label: "Auto-organized", icon: FolderCheck },
                { value: "0", label: "Manual effort", icon: Target },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.2 }}
                  className="text-center"
                >
                  <stat.icon className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
                  <p className="text-5xl text-white mb-2">{stat.value}</p>
                  <p className="text-gray-400">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section> */}
      <section className="ThreeCategoriesSection">
        <div className="ThreeCategoriesSection__gridBg" />

        {/* Glow */}
        {/* <div className="ThreeCategoriesSection__glow" /> */}

        <div className="ThreeCategoriesSection__container">
          <div className="ThreeCategoriesSection__content">
            <Target className="ThreeCategoriesSection__icon" />

            <h2 className="ThreeCategoriesSection__heading">
              Professional Power.
              <br />
              <span className="ThreeCategoriesSection__gradientText">
                Effortless Control.
              </span>
            </h2>

            <p className="ThreeCategoriesSection__para">
              Enterprise-grade document management that works automatically.
              <br />
              No training required. No complexity. Just results.
            </p>

            <div className="ThreeCategoriesSection__grid">
              {/* map items */}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
